import { useCallback, useState } from 'react';

import { env } from '@/env';
import { qgammonAuthHeaders } from '@/lib/qgammon/auth';
import type { MatchStatus } from '@/lib/qgammon/matchStatus';
import type { Player } from '@qlashgeminiz/qgammon-rules';
import { corpoAccettazione } from '@/lib/qgammon/allenamentoInvito';

/**
 * **La chiamata `POST /invites/:code/accept`, in un posto solo** (I-252, T1-7).
 *
 * Estratta da `InviteLanding.tsx` (dove viveva prima, unico chiamante) perche' la carta-sfida nel
 * DM deve accettare con lo stesso comportamento — stessi rifiuti (scaduto/proprio/gia' preso, tuo o
 * dell'avversario troppe partite), senza ricopiarli: un secondo parser della stessa risposta HTTP
 * diverge dal primo alla prima modifica non fatta in entrambi. Chi chiama traduce `motivo` nella
 * PROPRIA copy — `InviteLanding` ha gia' la sua (`Refusal`/`refusalFor404`, che sa distinguere
 * `expired` da `gone` guardando la preview che SOLO lei tiene); la carta nel DM non ha bisogno di
 * quella finezza e mostra `motivo` piu' o meno direttamente.
 *
 * 🔴 `status` NON e' un extra (ADR-0114): accettare crea un APPUNTAMENTO, il server lo dichiara qui,
 * e chi chiama non deve leggere solo `{matchId, color}` — v. il commento gemello in `InviteLanding`.
 */

export type EsitoAccettaInvito =
  | { ok: true; matchId: string; color: Player; status?: MatchStatus }
  | { ok: false; motivo: 'proprio' }
  | { ok: false; motivo: 'non-trovato' }
  | { ok: false; motivo: 'troppe-partite'; chi: 'creatore' | 'tu' }
  | { ok: false; motivo: 'irraggiungibile' };

export interface UsoAccettaInvito {
  accetta: (code: string) => Promise<EsitoAccettaInvito>;
  accettando: boolean;
}

export function useAccettaInvito(): UsoAccettaInvito {
  const [accettando, setAccettando] = useState(false);

  const accetta = useCallback(async (code: string): Promise<EsitoAccettaInvito> => {
    setAccettando(true);
    try {
      const res = await fetch(`${env.qgammonWsUrl}/invites/${encodeURIComponent(code)}/accept`, {
        method: 'POST',
        headers: await qgammonAuthHeaders(true),
        // Allenamento fra amici (cantiere 34): la risposta ESPLICITA per QUESTO codice, se chi chiama l'ha registrata (`registraRisposta`); senza,
        // `{}` = senza allenamento. Ramo del letterale: in produzione il corpo e' quello di sempre (V11, P4 = 0).
        body: JSON.stringify(import.meta.env.VITE_FEATURE_BACKGAMMON_COACH_ENABLED === 'true' ? corpoAccettazione(code) : {}),
      });
      if (res.status === 400) {
        const body = (await res.json().catch(() => ({}))) as { error?: string };
        return body.error === 'cannot_accept_own' ? { ok: false, motivo: 'proprio' } : { ok: false, motivo: 'irraggiungibile' };
      }
      if (res.status === 404) return { ok: false, motivo: 'non-trovato' };
      if (res.status === 409) {
        const body = (await res.json().catch(() => ({}))) as { error?: string; who?: string };
        if (body.error !== 'too_many_active_matches') return { ok: false, motivo: 'irraggiungibile' };
        return { ok: false, motivo: 'troppe-partite', chi: body.who === 'creator' ? 'creatore' : 'tu' };
      }
      if (!res.ok) return { ok: false, motivo: 'irraggiungibile' };
      const body = (await res.json()) as { matchId: string; color: Player; status?: MatchStatus };
      return { ok: true, matchId: body.matchId, color: body.color, status: body.status };
    } catch {
      return { ok: false, motivo: 'irraggiungibile' };
    } finally {
      setAccettando(false);
    }
  }, []);

  return { accetta, accettando };
}
